import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, Users, CreditCard, LogOut, Bell, CheckCheck, Factory, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { timeAgo } from "@/lib/format";

const NAV = [
  { to: "/enterprise/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/enterprise/buyers", label: "Buyers & Team", icon: Users },
  { to: "/enterprise/supplier-requests", label: "Supplier Requests", icon: Factory },
  { to: "/enterprise/support", label: "Support", icon: LifeBuoy },
  { to: "/enterprise/subscription", label: "Subscription", icon: CreditCard },
];

export default function EnterpriseLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-5 px-4">
          <div className="flex items-center gap-2">
            <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-7 w-auto" />
            <span className="ml-1 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Enterprise</span>
          </div>
          <nav className="hidden items-center gap-1 lg:flex" data-testid="enterprise-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`enterprise-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors duration-150 ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`
                }>
                <n.icon className="h-3.5 w-3.5" />{n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <DropdownMenu onOpenChange={(o) => o && loadNotifs()}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="relative" data-testid="enterprise-notifications-bell">
                  <Bell className="h-4 w-4" />
                  {notifs.unread > 0 && (
                    <span data-testid="enterprise-unread-count" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                      {notifs.unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96 bg-white">
                <DropdownMenuLabel className="flex items-center justify-between">
                  Notifications
                  <button data-testid="enterprise-mark-all-read" className="flex items-center gap-1 text-xs font-normal text-blue-700 hover:underline"
                    onClick={() => api.post("/notifications/read-all").then(loadNotifs)}>
                    <CheckCheck className="h-3 w-3" /> Mark all read
                  </button>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="max-h-80 overflow-y-auto">
                  {notifs.notifications.length === 0 && <p className="p-4 text-sm text-muted-foreground">No notifications yet.</p>}
                  {notifs.notifications.slice(0, 15).map((n) => (
                    <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-0.5 p-3 ${!n.read ? "bg-blue-50/60" : ""}`}>
                      <span className="text-sm font-medium">{n.title}</span>
                      <span className="text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{timeAgo(n.created_at)}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="hidden text-right md:block">
              <p className="max-w-40 truncate text-sm font-medium leading-tight">{user?.name}</p>
              <p className="text-xs text-muted-foreground leading-tight">Enterprise Admin</p>
            </div>
            <Button variant="outline" size="icon" data-testid="enterprise-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl items-center gap-1 overflow-x-auto px-4 pb-2 lg:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium ${isActive ? "bg-slate-900 text-white" : "text-slate-600"}`
              }>
              <n.icon className="h-3.5 w-3.5" />{n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl p-6">
        <Outlet />
      </main>
    </div>
  );
}
